import { useMemo, useRef } from "react";
import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import Avatar from "components/common/Avatar";
import { getAttendanceList } from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import { EmployeeAttendanceLocationType } from "features/EmployeeAttendance/constants/employee-attendance.constants";
import type { AttendanceListRow } from "features/EmployeeAttendance/types/employee-attendance.types";
import moment from "moment";

type Props = { refreshRef?: React.MutableRefObject<(() => void) | null> };

function resolveName(row: AttendanceListRow) {
  const first = row.user?.firstName ?? "";
  const last = row.user?.lastName ?? "";
  return [first, last].filter(Boolean).join(" ").trim() || row.code || "Unknown";
}

export function WfhTodayCard({ refreshRef }: Props = {}) {
  const today = moment().format("YYYY-MM-DD");
  const internalRef = useRef<(() => void) | null>(null);
  const tableRef = refreshRef ?? internalRef;

  const requestParams = useMemo(
    () => ({ date: today, inLocationType: EmployeeAttendanceLocationType.WORK_FROM_HOME }),
    [today],
  );

  const columns: ColumnDef<AttendanceListRow>[] = useMemo(
    () => [
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const name = resolveName(row.original);
          const avatar = row.original.user?.avatar ?? undefined;
          const code = row.original.code;
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="flex-shrink-0 avatar-xs">
                <Avatar
                  name={name}
                  image={avatar}
                  className="rounded-circle w-100 h-100"
                />
              </div>
              <div className="min-w-0">
                <div className="fw-medium text-truncate small">{name}</div>
                {code ? (
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    {code}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Department",
        id: "department",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            {row.original.department?.name ?? "—"}
          </span>
        ),
      },
      {
        header: "In time",
        id: "inTime",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const t = row.original.attendance?.inTime;
          if (!t) return <span className="text-muted small">—</span>;
          return (
            <span className="small text-muted">
              {moment(t).format("hh:mm A")}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">Working from home</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
            Work from home — today
          </p>
        </div>
        <i className="ri-home-office-line fs-20 text-muted" />
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getAttendanceList}
          requestParams={requestParams}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          refreshRef={tableRef}
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
